/** Amazon caps one advertising account at this many portfolios. */
export const PORTFOLIO_LIMIT = 100

/** Amazon's portfolio name length cap. */
export const PORTFOLIO_NAME_MAX_LENGTH = 100

export interface ParsedPortfolioNames {
    /** Names that will be sent to Amazon, in input order. */
    create: string[]
    /** Names collapsed because an earlier line already used them. */
    duplicates: string[]
    /** Names the account already uses, which the server will skip. */
    existing: string[]
    /** Names longer than Amazon accepts. */
    tooLong: string[]
}

/**
 * Parse a pasted list into the buckets the preview shows.
 *
 * Splits on newlines ONLY. Portfolio names may contain commas, so the
 * comma-splitting the ASIN modal uses would silently cut names in half. This
 * mirrors `parsePortfolioNames` in the TS engine resolver, which is
 * authoritative — the preview exists so the user is not surprised by it.
 */
export const parsePortfolioNameInput = (
    input: string,
    existingNames: string[] = [],
): ParsedPortfolioNames => {
    const existingKeys = new Set(existingNames.map((name) => name.trim().toLowerCase()))
    const seen = new Set<string>()
    const create: string[] = []
    const duplicates: string[] = []
    const existing: string[] = []
    const tooLong: string[] = []

    for (const line of input.split('\n')) {
        const name = line.trim()
        if (name === '') {
            continue
        }

        const key = name.toLowerCase()
        if (seen.has(key)) {
            duplicates.push(name)
            continue
        }
        seen.add(key)

        if (name.length > PORTFOLIO_NAME_MAX_LENGTH) {
            tooLong.push(name)
            continue
        }
        if (existingKeys.has(key)) {
            existing.push(name)
            continue
        }
        create.push(name)
    }

    return { create, duplicates, existing, tooLong }
}

export const exceedsPortfolioLimit = (existingCount: number, newCount: number): boolean =>
    existingCount + newCount > PORTFOLIO_LIMIT
